/**
 * @fileoverview Lazy loader for TradingView library.
 * Only loads the external script when actually needed.
 * @module external/tradingview/loader
 */

const TV_SCRIPT_URL = 'https://s3.tradingview.com/tv.js';

/** @type {boolean} */
let loaded = false;

/** @type {Promise<void>|null} */
let loadPromise = null;

/**
 * Lazily loads the TradingView widget library.
 * Safe to call multiple times - only loads once.
 * @returns {Promise<void>}
 * @throws {Error} If script fails to load
 */
export function loadTradingView() {
  if (loaded) {
    console.log('[TradingView] Already loaded');
    return Promise.resolve();
  }
  
  if (loadPromise) {
    console.log('[TradingView] Load in progress, waiting...');
    return loadPromise;
  }
  
  console.log('[TradingView] Loading external script...');
  
  loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = TV_SCRIPT_URL;
    script.async = true;
    
    script.onload = () => {
      loaded = true;
      console.log('[TradingView] Script loaded successfully');
      resolve();
    };
    
    script.onerror = () => {
      loadPromise = null;
      const error = new Error('Failed to load TradingView script');
      console.error('[TradingView]', error.message);
      reject(error);
    };
    
    document.head.appendChild(script);
  });
  
  return loadPromise;
}

/**
 * Checks if TradingView is currently loaded.
 * @returns {boolean}
 */
export function isTradingViewLoaded() {
  return loaded && typeof window.TradingView !== 'undefined';
}
